import React, { useState, useEffect } from 'react';
import {
  Container,
  Header,
  SpaceBetween,
  FormField,
  Alert,
  Button,
  Box,
  TokenGroup,
  ColumnLayout,
} from '@cloudscape-design/components';
import RequiredFieldLabel from '../../RequiredFieldLabel';
import { QuestionFormParams } from '../QuestionFormWizard';
import MultiSelectTags from '../../tag/MultiSelectTags';
import { Location } from 'src/types/helios/Location';
import LocationSelectionPanel from '../../location/LocationSelectionPanel';
import { useSplitPanel } from 'src/context/SplitPanelContext';
import { AuditTooltipMessages } from 'src/contstant/commonLabels';
import Tooltip from 'src/components/Tooltip';
import { transformCodesToLocations, extractCodes } from 'src/helper/locationHelper';

interface TagsProps {
  formData: QuestionFormParams;
  setFormData: React.Dispatch<React.SetStateAction<QuestionFormParams>>;
  fieldValidationError: any;
  tagsList: any[];
  locationsList: Location[];
}

const Tags: React.FC<TagsProps> = ({
  formData,
  setFormData,
  fieldValidationError,
  tagsList,
  locationsList,
}) => {
  const { setContent, setTitle, closeView, isOpen } = useSplitPanel();

  useEffect(() => {
    return () => {
      closeView();
    };
  }, []);

  return (
    <SpaceBetween size="xs" data-testid="tags-space-between">
      <SpaceBetween size="l" data-testid="tags-inner-space-between">
        {(() => {
          const missingTags = tagsList.filter(
            (tag) =>
              tag.isMandatoryForQuestions && !formData.tagsIncluded?.[tag.tagGroupName],
          );
          return (
            missingTags.length > 0 && (
              <Alert
                data-testid="missing-tags-alert"
                type="warning"
                header="Missing mandatory tags"
              >
                Please select: {missingTags.map((tag) => tag.tagGroupName).join(', ')}
              </Alert>
            )
          );
        })()}
        {tagsList.length < 1 && (
          <Alert data-testid="no-tags-alert" type="info" header="Note:">
            Please create a Tag to create questions
          </Alert>
        )}
        <FormField
          data-testid="tags-included-field"
          label={<RequiredFieldLabel label="Tags Included" />}
          errorText={fieldValidationError.tagsIncluded}
          info={<Tooltip content={AuditTooltipMessages.TAGS_INCLUDED} />}
        >
          <MultiSelectTags
            data-testid="tags-included-select"
            tags={tagsList}
            isLoading={false}
            error={null}
            excludedItems={Object.entries(formData.tagsExcluded || {}).map(
              ([tagGroupName, tagValues]) => ({ tagGroupName, tagValues }),
            )}
            selectedTagValues={Object.entries(formData.tagsIncluded || {}).map(
              ([tagGroupName, tagValues]) => ({ tagGroupName, tagValues }),
            )}
            onItemSelection={(selectedTags) => {
              const tagsMap: Record<string, string[]> = {};
              selectedTags.forEach((tag) => {
                tagsMap[tag.tagGroupName] = tag.tagValues;
              });
              setFormData((prev) => ({ ...prev, tagsIncluded: tagsMap }));
            }}
          />
        </FormField>

        {formData.tagsIncluded && Object.entries(formData.tagsIncluded).length > 0 && (
          <FormField
            data-testid="tags-excluded-field"
            label="Tags Excluded"
            info={<Tooltip content={AuditTooltipMessages.TAGS_EXCLUDED} />}
          >
            <MultiSelectTags
              data-testid="tags-excluded-select"
              tags={tagsList}
              isLoading={false}
              error={null}
              selectedTagValues={Object.entries(formData.tagsExcluded || {}).map(
                ([tagGroupName, tagValues]) => ({ tagGroupName, tagValues }),
              )}
              excludedItems={Object.entries(formData.tagsIncluded || {}).map(
                ([tagGroupName, tagValues]) => ({ tagGroupName, tagValues }),
              )}
              onItemSelection={(selectedTags) => {
                const tagsMap: Record<string, string[]> = {};
                selectedTags.forEach((tag) => {
                  tagsMap[tag.tagGroupName] = tag.tagValues;
                });
                setFormData((prev) => ({ ...prev, tagsExcluded: tagsMap }));
              }}
            />
          </FormField>
        )}
      </SpaceBetween>

      {/* Locations Section */}
      <ColumnLayout columns={8}>
        <h4 className="locations-title">
          Locations excluded
          <Tooltip content={AuditTooltipMessages.LOCATIONS_EXCLUDED} />
          <Button
            data-testid="add-location-button"
            iconName="add-plus"
            onClick={() => {
              setTitle('Select Locations');
              setContent(
                <LocationSelectionPanel
                  selectedLocations={transformCodesToLocations(
                    formData.locationsExcluded,
                    locationsList,
                  )}
                  onChange={(locations: Location[]) => {
                    setFormData((prevState) => ({
                      ...prevState,
                      locationsExcluded: extractCodes(locations),
                    }));
                  }}
                  locations={locationsList}
                />,
              );
            }}
          >
            Exclude Location
          </Button>
        </h4>
      </ColumnLayout>

      {formData.locationsExcluded && formData.locationsExcluded.length > 0 ? (
        <TokenGroup
          data-testid="locations-token-group"
          onDismiss={
            isOpen
              ? undefined
              : ({ detail: { itemIndex } }) => {
                  setFormData((prev) => ({
                    ...prev,
                    locationsExcluded: [
                      ...prev.locationsExcluded.slice(0, itemIndex),
                      ...prev.locationsExcluded.slice(itemIndex + 1),
                    ],
                  }));
                }
          }
          items={formData.locationsExcluded.map((code) => ({
            label: code,
            dismissLabel: isOpen ? '' : `Remove ${code}`,
          }))}
        />
      ) : (
        <Box data-testid="no-locations-message" color="text-status-inactive">
          No locations excluded
        </Box>
      )}
    </SpaceBetween>
  );
};

export default Tags;
